import React, { Fragment, useEffect, useState } from "react";
import { Box, Button, Grid2 as Grid, LinearProgress, Stack, Typography } from "@mui/material";
import { LAYOUT_PADDING } from "../../../enums/themeEnum";
import { GenericIconButton } from "../../generic/GenericIconButton";
import { CheckCircle, List } from "@phosphor-icons/react";
import { SearchField } from "../../generic/GenericTextFields";
import { SIGN_IN_FORMS } from "../../../enums/signInFormEnums";
import { SignUpDialog } from "./signup/SignUpDialog";
import { updateEmail, verify } from "../../../config/apisauce";
import { LoginDialog } from "./login/LoginDialog";
import { LOGIN_FORMS } from "../../../enums/loginFormEnums";
import { ProfileDropDown } from "./ProfileDropDown";
import { router, usePage } from "@inertiajs/react";
import useStore from "../../../states/store";
import { GUEST_ROUTES } from "../../../routes/PageList";
import { GenericDialog } from "../../generic/GenericDialog";
import { NeedHelp } from "./generic/GenericDialogFooter";
import { GenericCircleIcon } from "../../generic/GenericCircleIcon";
import { useLogoutUser } from "../../generic/functions/genericFunctions";
import { COLLAB_KEY } from "../../../enums/generalEnums";

export const Topbar = ({ handleToggleDrawer, userDetails }) => {
  const handleLogout = useLogoutUser();
  const [
    { loaderState, signUpDialogState, loginDialogState, referrer },
    { setPopularTag, setSearchValue, setCreatorFilters, setSignUpDialogState, setLoginDialogState },
  ] = useStore();
  const { auth } = usePage().props;
  const { url } = usePage();
  const verified = auth?.user !== null;
  const storedHash = sessionStorage.getItem("hashValue");
  const resetPasswordHash = sessionStorage.getItem("resetPasswordHash");
  const changeEmailHash = sessionStorage.getItem("changeEmailHash");
  const loginDialogStatus = sessionStorage.getItem("loginDialogStatus");
  const pathName = new URL(url, window.location.origin).pathname;
  const [selectedSignInForm, setSelectedSignForm] = useState(SIGN_IN_FORMS[0]);
  const [selectedLoginForm, setSelectedLoginForm] = useState(LOGIN_FORMS[0]);
  const [openLoginDialog, setOpenLoginDialog] = useState(loginDialogStatus ?? false);
  const [openChangeEmailDialog, setOpenChangeEmailDialog] = useState(false);
  const [search, setSearch] = useState('');

  const verifyAccount = async () => {
    const response = await verify({ hash: storedHash });
    if (response.ok) {
      setSignUpDialogState(true);
      setSelectedSignForm(SIGN_IN_FORMS[3]);
    } else {
      sessionStorage.removeItem('hashValue');
    }
    router.visit('/');
  };

  const handleUpdateEmail = async () => {
    const reponse = await updateEmail({ hash: changeEmailHash });
    if (reponse.ok) {
      setOpenChangeEmailDialog(true);
      handleLogout();
      router.visit('/');
    }
  };

  const handleCloseSignUpDialog = () => {
    setSignUpDialogState(false);
    if (storedHash) sessionStorage.removeItem('hashValue');
  };

  const handleCloseLoginDialog = () => {
    setOpenLoginDialog(false);
    setLoginDialogState(false);
    if (resetPasswordHash) sessionStorage.removeItem('resetPasswordHash');
    if (loginDialogStatus) sessionStorage.removeItem('loginDialogStatus');
  };

  const handleCloseChangeEmailDialog = () => {
    setOpenChangeEmailDialog(false);
    if (changeEmailHash) sessionStorage.removeItem('changeEmailHash');
  };

  const handleOpenSignUpDialog = () => {
    setSelectedSignForm(SIGN_IN_FORMS[0]);
    setSignUpDialogState(true);
    setOpenLoginDialog(false);
    setLoginDialogState(false);
  };

  const handleOpenLoginDialog = () => {
    setSelectedLoginForm(LOGIN_FORMS[0]);
    handleCloseSignUpDialog();
    setOpenLoginDialog(true);
    handleCloseChangeEmailDialog();
  };

  const handleRedirectToHome = () => {
    router.visit('/');
    setPopularTag(COLLAB_KEY);
  };

  const handleSearch = (isClear = false) => {
    router.visit('/collab-creators');
    setSearchValue(isClear ? '' : search);
    setPopularTag(COLLAB_KEY);
    setCreatorFilters({});
    if (isClear) setSearch('');
  };

  const TopBarButtons = ({ gap = 1 }) => {
    return (
      <Stack direction={"row"} gap={gap}>
        <Button
          variant="contained"
          sx={{ backgroundColor: "#FFF", color: "#02AFF0 !important", fontSize: { xs: "12px", xsm: "14px" }, minWidth: 85 }}
          onClick={() => handleOpenSignUpDialog()}
        >
          Join Free
        </Button>
        <Button
          variant="outlined"
          sx={{ color: "#FFF", borderColor: "#FFF", fontSize: { xs: "12px", xsm: "14px" }, minWidth: 85 }}
          onClick={() => handleOpenLoginDialog()}
        >
          Login
        </Button>
      </Stack>
    );
  };

  useEffect(() => {
    if (storedHash) {
      verifyAccount();
    }
    if (resetPasswordHash) {
      router.visit('/');
      setOpenLoginDialog(true);
      setSelectedLoginForm(LOGIN_FORMS[3]);
    }
    if (changeEmailHash) {
      handleUpdateEmail();
    }
    if (referrer && pathName === '/register') {
      router.visit('/');
    }
  }, [storedHash, resetPasswordHash, changeEmailHash, referrer]);

  return (
    <Fragment>
      <Box sx={{ backgroundColor: "primary.main", px: { xs: 2, sm: 3 }, py: 2 }}>
        <Grid container flexGrow={1} justifyContent={'space-between'} alignItems={'center'}>
          <Grid item>
            <Box sx={{ display: { xs: 'flex', md: 'none' }, height: '35px', }} onClick={() => handleRedirectToHome()}>
              <img src="/logos/wannacollab-logo-white.svg" alt="WannaCollab" />
            </Box>
            {GUEST_ROUTES.includes(pathName) && (
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <SearchField
                  value={search}
                  placeholder="Search for creators"
                  disableSearchButton={false}
                  handleChange={(value) => setSearch(value)}
                  searchButtonAction={() => handleSearch()}
                  handleClear={() => handleSearch(true)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                />
              </Box>
            )}
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              {verified ? (
                <ProfileDropDown user={userDetails} />
              ) : (
                <Box sx={{ display: { xs: "none", sm: "flex" } }}>
                  <TopBarButtons />
                </Box>
              )}
              <Box sx={{ display: { xs: "flex", sm: "none" }, mb: 0.7 }}>
                <Stack direction={"row"}>
                  {!verified && <TopBarButtons gap={0.5} />}
                  <GenericIconButton icon={<List color={"#FFF"} size={20} />} onClick={() => handleToggleDrawer()} />
                </Stack>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      {loaderState && <LinearProgress color="info" />}
      <SignUpDialog
        selectedForm={selectedSignInForm}
        setSelectedForm={setSelectedSignForm}
        openSignInAndUpDialog={signUpDialogState}
        handleCloseDialog={() => handleCloseSignUpDialog()}
        handleOpenLoginDialog={() => handleOpenLoginDialog()}
      />
      <LoginDialog
        selectedForm={selectedLoginForm}
        setSelectedForm={setSelectedLoginForm}
        openLoginDialog={openLoginDialog || loginDialogState}
        handleCloseDialog={() => handleCloseLoginDialog()}
        handleOpenSignUpDialog={() => handleOpenSignUpDialog()}
        handleOpenLoginDialog={() => handleOpenLoginDialog()}
      />
      <GenericDialog
        title={'Email Verified'}
        width={'550px'}
        openDialog={openChangeEmailDialog}
        handleClose={() => handleCloseChangeEmailDialog()}
      >
        <Box display={'flex'} alignItems={'center'} justifyContent={'center'} mb={3}>
          <Stack gap={2} alignItems={'center'}>
            <GenericCircleIcon color="primary.main">
              <CheckCircle size={96} weight="thin" />
            </GenericCircleIcon>
            <Typography variant="subtitle1" textAlign={'center'}>
              Thanks for verifying! <br></br>
              You may now use your new email to log in.
            </Typography>
            <Button variant="contained" className="longButton" onClick={() => handleOpenLoginDialog()}>Login</Button>
          </Stack>
        </Box>
        <NeedHelp />
      </GenericDialog>
    </Fragment>
  );
};
